/**
 * O Dev Board: um quadro cujas colunas o time cria, renomeia e move.
 *
 * <p><b>Não é o Kanban do Roadmap com outro nome.</b> Lá as colunas são status derivado —
 * "em atraso" sai da janela de meses, e ninguém arrasta um card para lá. Aqui a coluna é
 * dado: o card está nela porque alguém o pôs, e é isso que permite criá-las e movê-las.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../api/cliente';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { ModalCard } from './ModalCard';
import { CHAVE_QUADRO, SLUG } from './tipos';
import type { Quadro } from './tipos';
import { Avatar } from '../../comum/Avatar';
import { CORES } from './cores';
import './devboard.css';

const CHAVE = CHAVE_QUADRO;


export function DevBoard() {
  const fila = useQueryClient();
  const navegar = useNavigate();

  /**
   * Qual card está aberto — a URL é quem manda, como no Roadmap.
   *
   * <p>`?card=12` abre o card 12. É assim que uma notificação leva direto ao que ela
   * está avisando: sem isso, o clique só trocaria de página e deixaria a pessoa
   * procurando o card na coluna certa. O endereço também fica copiável.
   */
  const [busca, setBusca] = useSearchParams();
  const aberto = busca.get('card') ? Number(busca.get('card')) : null;
  const setAberto = (id: number | null) =>
    setBusca(
      (atual) => {
        const nova = new URLSearchParams(atual);
        if (id == null) nova.delete('card');
        else nova.set('card', String(id));
        return nova;
      },
      { replace: true }
    );
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<{ colunaId: number; aposId: number | null } | null>(null);
  const [editandoColuna, setEditandoColuna] = useState<number | null>(null);
  const [novaColuna, setNovaColuna] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [criandoEm, setCriandoEm] = useState<number | null>(null);
  const [nomeDoCard, setNomeDoCard] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const trilho = useRef<HTMLDivElement>(null);

  const { data, isPending, error } = useQuery<Quadro>({
    queryKey: CHAVE,
    queryFn: () => api(`/api/projetos/${SLUG}/quadro`),
  });

  /**
   * Toda escrita devolve o quadro inteiro, e o cache é substituído por ele.
   *
   * <p>Mover um card muda a ordem de duas colunas ao mesmo tempo; recompor isso no
   * cliente é onde o kanban costuma divergir do servidor. O quadro é pequeno — trocar
   * tudo custa menos que manter dois estados em sincronia.
   */
  const aplicar = (novo: Quadro) => fila.setQueryData(CHAVE, novo);
  const falhar = (e: Error) => setErro(e.message);

  /**
   * Abrir o card marca que você o viu.
   *
   * <p>É o gesto que apaga a bolinha — e ele é o gesto de <b>abrir</b>, não o de fechar:
   * quem abre e lê metade leu; quem fecha sem ler já tinha aberto. Esperar o fechamento
   * deixaria o aviso aceso em quem trocou de aba com o card aberto.
   *
   * <p>Não recarrega o quadro depois. A bolinha some no próximo carregamento da página,
   * e apagá-la debaixo do dedo — enquanto a pessoa lê — piscaria a coluna inteira por
   * uma informação que ela acabou de consumir. O `cache` local é atualizado à mão, que
   * custa nada e não mexe em mais nada da tela.
   *
   * <p>Falha em silêncio de propósito: não conseguir anotar uma leitura não é algo que
   * mereça um alerta vermelho por cima do quadro — o pior caso é a bolinha continuar
   * acesa, que é o estado de onde ela veio.
   */
  useEffect(() => {
    if (aberto == null) return;
    void api(`/api/tarefas/${aberto}/vista`, { method: 'POST' })
      .then(() =>
        fila.setQueryData<Quadro>(CHAVE, (q) =>
          !q
            ? q
            : {
                ...q,
                colunas: q.colunas.map((c) => ({
                  ...c,
                  cards: c.cards.map((card) =>
                    card.id === aberto ? { ...card, naoVisto: false } : card
                  ),
                })),
              }
        )
      )
      .catch(() => {});
  }, [aberto, fila]);

  const criarColuna = useMutation({
    mutationFn: (nome: string) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/colunas`, {
        method: 'POST',
        body: JSON.stringify({ nome, cor: CORES[(data?.colunas.length ?? 0) % CORES.length] }),
      }),
    onSuccess: (q) => {
      aplicar(q);
      setNovaColuna(false);
      setNomeNovo('');
      // A coluna nasce no fim: rolar até lá é o que mostra que ela existe.
      setTimeout(() => trilho.current?.scrollTo({ left: trilho.current.scrollWidth }), 60);
    },
    onError: falhar,
  });

  const editarColuna = useMutation({
    mutationFn: ({ id, nome, cor }: { id: number; nome?: string; cor?: string }) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/colunas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ nome, cor }),
      }),
    onSuccess: (q) => {
      aplicar(q);
      setEditandoColuna(null);
    },
    onError: falhar,
  });

  const excluirColuna = useMutation({
    mutationFn: (id: number) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/colunas/${id}`, { method: 'DELETE' }),
    onSuccess: aplicar,
    onError: falhar,
  });

  const reordenarColunas = useMutation({
    mutationFn: (ids: number[]) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/colunas`, {
        method: 'PATCH',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: aplicar,
    onError: falhar,
  });

  const criarCard = useMutation({
    mutationFn: ({ nome, colunaId }: { nome: string; colunaId: number }) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/cards`, {
        method: 'POST',
        body: JSON.stringify({ nome, colunaId }),
      }),
    onSuccess: (q) => {
      aplicar(q);
      setNomeDoCard('');
      setCriandoEm(null);
    },
    onError: falhar,
  });

  const moverCard = useMutation({
    mutationFn: ({ id, colunaId, aposCardId }: { id: number; colunaId: number; aposCardId: number | null }) =>
      api<Quadro>(`/api/projetos/${SLUG}/quadro/cards/${id}/posicao`, {
        method: 'PATCH',
        body: JSON.stringify({ colunaId, aposCardId }),
      }),
    onSuccess: aplicar,
    onError: falhar,
  });

  if (isPending) return <div className="rm-estado">carregando o quadro…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;

  const membros = new Map(data.membros.map((m) => [m.id, m]));
  const total = data.colunas.reduce((s, c) => s + c.cards.length, 0);

  /**
   * O card do modal, relido do quadro a cada render.
   *
   * <p>Guardar o objeto no estado congelaria o cabeçalho: trocar de coluna ou marcar um
   * responsável devolve o quadro novo, e o modal continuaria mostrando o antigo. Guardamos
   * só o id.
   */
  const cardAberto = (() => {
    for (const coluna of data.colunas) {
      const achado = coluna.cards.find((c) => c.id === aberto);
      if (achado) return { ...achado, colunaId: coluna.id };
    }
    return null;
  })();

  /** Troca a coluna de lugar com a vizinha. Mandamos a ordem inteira, já resolvida. */
  function moverColuna(id: number, direcao: -1 | 1) {
    const ids = data!.colunas.map((c) => c.id);
    const de = ids.indexOf(id);
    const para = de + direcao;
    if (para < 0 || para >= ids.length) return;
    ids.splice(para, 0, ids.splice(de, 1)[0]);
    reordenarColunas.mutate(ids);
  }

  return (
    <div className="db">
      <TituloDaPagina
        acoes={
          <button className="db-btn fantasma" onClick={() => navegar('/changelog')}>
            📜 Changelog
          </button>
        }
      >
        O quadro do time de desenvolvimento. As colunas são suas: crie, renomeie e mova
        conforme o fluxo mudar. {total} card{total === 1 ? '' : 's'} em {data.colunas.length}{' '}
        colunas.
      </TituloDaPagina>

      {/* O progresso do quadro, que não é o do roadmap: aqui a medida é a semana do time
          de dev, lá são os doze meses do plano. Os dois convivem sem se somar. */}
      {data.progresso.total > 0 && (
        <div className="db-progresso">
          <div className="db-prog-trilho">
            <div className="db-prog-fill" style={{ width: `${data.progresso.pct}%` }} />
          </div>
          <span className="db-prog-n">
            <b>{data.progresso.pct}%</b>
            {data.progresso.concluidos} de {data.progresso.total} concluídos
          </span>
        </div>
      )}

      {erro && (
        <div className="db-erro" role="alert">
          {erro}
          <button onClick={() => setErro(null)} aria-label="Fechar">×</button>
        </div>
      )}

      {/* A barra rola na horizontal porque o quadro cresce para o lado — foi o pedido:
          "vai ter bem mais colunas". */}
      <div className="db-trilho" ref={trilho}>
        {data.colunas.map((coluna, i) => (
          <section
            key={coluna.id}
            className={`db-col${alvo?.colunaId === coluna.id ? ' over' : ''}`}
            style={{ '--c': coluna.cor ?? 'var(--gold)' } as React.CSSProperties}
            onDragOver={(e) => {
              if (arrastando == null) return;
              e.preventDefault();
              // Sem card sob o cursor, a coluna inteira é o alvo: solta no fim.
              if (!alvo || alvo.colunaId !== coluna.id) {
                setAlvo({ colunaId: coluna.id, aposId: coluna.cards.at(-1)?.id ?? null });
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setAlvo(null);
            }}
            onDrop={() => {
              if (arrastando != null && alvo) {
                moverCard.mutate({
                  id: arrastando,
                  colunaId: alvo.colunaId,
                  aposCardId: alvo.aposId,
                });
              }
              setArrastando(null);
              setAlvo(null);
            }}
          >
            <header className="db-col-topo">
              <span className="db-risco" />
              {editandoColuna === coluna.id ? (
                <input
                  className="db-col-nome-in"
                  defaultValue={coluna.nome}
                  autoFocus
                  onBlur={(e) => {
                    const novo = e.target.value.trim();
                    if (novo && novo !== coluna.nome) {
                      editarColuna.mutate({ id: coluna.id, nome: novo });
                    } else setEditandoColuna(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setEditandoColuna(null);
                  }}
                />
              ) : (
                <button
                  className="db-col-nome"
                  onClick={() => setEditandoColuna(coluna.id)}
                  title="Clique para renomear"
                >
                  {coluna.nome}
                </button>
              )}
              <span className="db-col-n">{coluna.cards.length}</span>

              <span className="db-col-acoes">
                {/* Setas, e não arrastar: mover coluna é raro e preciso — uma seta acerta
                    de primeira, e funciona no teclado. */}
                <button
                  onClick={() => moverColuna(coluna.id, -1)}
                  disabled={i === 0}
                  title="Mover para a esquerda"
                  aria-label={`Mover ${coluna.nome} para a esquerda`}
                >
                  ‹
                </button>
                <button
                  onClick={() => moverColuna(coluna.id, 1)}
                  disabled={i === data.colunas.length - 1}
                  title="Mover para a direita"
                  aria-label={`Mover ${coluna.nome} para a direita`}
                >
                  ›
                </button>
                <button
                  className="db-del"
                  onClick={() => excluirColuna.mutate(coluna.id)}
                  title="Apagar coluna"
                  aria-label={`Apagar ${coluna.nome}`}
                >
                  ×
                </button>
              </span>
            </header>

            <div className="db-cards">
              {coluna.cards.length === 0 && <div className="db-vazio">arraste um card aqui</div>}

              {coluna.cards.map((card) => (
                <article
                  key={card.id}
                  className={`db-card${arrastando === card.id ? ' drag' : ''}${
                    card.concluida ? ' feito' : ''
                  }${alvo?.colunaId === coluna.id && alvo.aposId === card.id ? ' solta' : ''}`}
                  draggable
                  onDragStart={() => setArrastando(card.id)}
                  onDragEnd={() => {
                    setArrastando(null);
                    setAlvo(null);
                  }}
                  onDragOver={(e) => {
                    if (arrastando == null || arrastando === card.id) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setAlvo({ colunaId: coluna.id, aposId: card.id });
                  }}
                  onClick={() => setAberto(card.id)}
                >
                  {/*
                    A bolinha do que você não viu.
                    Fica no canto do card, e não junto do nome: ela não é sobre o nome —
                    é sobre o card inteiro. E some ao abrir, que é o gesto de ler.
                  */}
                  {card.naoVisto && (
                    <span
                      className="db-nao-visto"
                      title="Você ainda não abriu este card, ou há comentário novo"
                      aria-label="não lido"
                    />
                  )}
                  <div className="db-card-nome">{card.nome}</div>
                  {/* O nome responde "o quê"; a descrição responde "qual" — é o que
                      separa dois cards de nome parecido sem obrigar a abrir os dois. */}
                  {card.descricao && <div className="db-card-desc">{card.descricao}</div>}
                  {card.tags.length > 0 && (
                    <div className="db-card-tags">
                      {card.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="db-etq"
                          style={{ '--c': tag.cor } as React.CSSProperties}
                        >
                          {tag.nome}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="db-card-pe">
                    {card.checklist.total > 0 && (
                      <span className="db-tag">
                        <span className="db-tag-ic">☑</span>
                        {card.checklist.feitos}/{card.checklist.total}
                      </span>
                    )}
                    {card.comentarios > 0 && (
                      <span className="db-tag">
                        <span className="db-tag-ic">💬</span>
                        {card.comentarios}
                      </span>
                    )}
                    <span className="db-avs">
                      {card.responsaveis.map((id) => {
                        const m = membros.get(id);
                        return m ? (
                          <Avatar key={id} className="db-av" nome={m.nome} cor={m.cor} url={m.avatar} />
                        ) : null;
                      })}
                    </span>
                  </div>
                </article>
              ))}

              {criandoEm === coluna.id ? (
                <div className="db-novo">
                  <textarea
                    value={nomeDoCard}
                    autoFocus
                    rows={2}
                    placeholder="O que precisa ser feito?"
                    onChange={(e) => setNomeDoCard(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (nomeDoCard.trim()) {
                          criarCard.mutate({ nome: nomeDoCard.trim(), colunaId: coluna.id });
                        }
                      }
                      if (e.key === 'Escape') {
                        setCriandoEm(null);
                        setNomeDoCard('');
                      }
                    }}
                  />
                  <div className="db-novo-acoes">
                    <button
                      className="db-btn"
                      disabled={!nomeDoCard.trim()}
                      onClick={() =>
                        criarCard.mutate({ nome: nomeDoCard.trim(), colunaId: coluna.id })
                      }
                    >
                      Adicionar
                    </button>
                    <button className="db-btn fantasma" onClick={() => setCriandoEm(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="db-mais"
                  onClick={() => {
                    setCriandoEm(coluna.id);
                    setNomeDoCard('');
                  }}
                >
                  + card
                </button>
              )}
            </div>
          </section>
        ))}

        {/* A coluna nova fica no fim do trilho, no lugar onde ela vai nascer. */}
        <section className="db-col nova">
          {novaColuna ? (
            <div className="db-novo">
              <input
                value={nomeNovo}
                autoFocus
                placeholder="Nome da coluna"
                onChange={(e) => setNomeNovo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nomeNovo.trim()) criarColuna.mutate(nomeNovo.trim());
                  if (e.key === 'Escape') setNovaColuna(false);
                }}
              />
              <div className="db-novo-acoes">
                <button
                  className="db-btn"
                  disabled={!nomeNovo.trim()}
                  onClick={() => criarColuna.mutate(nomeNovo.trim())}
                >
                  Criar
                </button>
                <button className="db-btn fantasma" onClick={() => setNovaColuna(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button className="db-nova-col" onClick={() => setNovaColuna(true)}>
              + coluna
            </button>
          )}
        </section>
      </div>

      {cardAberto && (
        <ModalCard
          card={cardAberto}
          colunas={data.colunas.map((c) => ({ id: c.id, nome: c.nome }))}
          membros={data.membros}
          tags={data.tags}
          slug={SLUG}
          aplicar={aplicar}
          fechar={() => setAberto(null)}
        />
      )}
    </div>
  );
}
